import React, { useState, useEffect } from "react";
import { Button, Icon, RadioGroup } from "@telia/teddy";
import _find from "lodash/find";
import { PickupPoint as PickupPointType } from "../../../../../../models/state";
import { If } from "../../../../components";
import TEXT from "../../../text-consts";
import PickupPoint from "../../atoms/PickupPoint/PickupPoint";
import "./PickupPointList.less";

const NEARBY_POINTS_TO_DISPLAY_AT_ONCE = 3;

interface PickupPointListProps {
    numberOfNearbyPointsToDisplay?: number;
    numberOfNearbyPointsToDisplayIncrement?: number;
    onPointSelected: (code: string) => void;
    pickupPoints: PickupPointType[];
    selectedPointCode: string;
}

const PickupPointList: React.FC<PickupPointListProps> = ({
    selectedPointCode,
    pickupPoints,
    numberOfNearbyPointsToDisplay = NEARBY_POINTS_TO_DISPLAY_AT_ONCE,
    numberOfNearbyPointsToDisplayIncrement = NEARBY_POINTS_TO_DISPLAY_AT_ONCE,
    onPointSelected,
}) => {
    const [pointsToDisplay, setPointsToDisplay] = useState(numberOfNearbyPointsToDisplay);
    const [points, setPoints] = useState<React.ReactNode[]>([]);

    useEffect(() => {
        // Create new array of points to show
        let includesSelectedPickupPoint = false;
        const newPoints: React.ReactNode[] = pickupPoints.reduce<React.ReactNode[]>((arr, point, index) => {
            if (index < pointsToDisplay) {
                if (point.code === selectedPointCode) {
                    includesSelectedPickupPoint = true;
                }
                arr.push(
                    <PickupPoint
                        key={`pickup-point-${point.code}`}
                        {...point}
                        isSelected={point.code === selectedPointCode}
                    />,
                );
            }
            return arr;
        }, []);

        const selectedPoint = _find(pickupPoints, (point) => point.code === selectedPointCode);
        if (selectedPoint && !includesSelectedPickupPoint) {
            // Replace last point in array with selected point
            newPoints.pop();
            newPoints.push(
                <PickupPoint
                    key={`pickup-point-${selectedPoint.code}`}
                    {...selectedPoint}
                    isSelected={selectedPoint.code === selectedPointCode}
                />,
            );
        }

        // Update points array
        setPoints(newPoints);
    }, [pointsToDisplay, onPointSelected, selectedPointCode, pickupPoints]);

    if (points.length === 0) {
        return null;
    }

    return (
        <div className="pickup-point-list">
            <RadioGroup
                value={selectedPointCode}
                onValueChange={onPointSelected}
                name="pickupPoint"
                className="pickup-point-list__radio-group"
            >
                {points}
            </RadioGroup>
            <div className="pickup-point-list__show-more">
                <If condition={pickupPoints.length > pointsToDisplay}>
                    <Button
                        className="pickup-point-list__show-more__button"
                        onClick={() => setPointsToDisplay(pointsToDisplay + numberOfNearbyPointsToDisplayIncrement)}
                        variant="text-underline"
                    >
                        {TEXT.SHOW_MORE_POINTS}
                        <Icon name="chevron-down" />
                    </Button>
                </If>
                <If condition={pointsToDisplay !== NEARBY_POINTS_TO_DISPLAY_AT_ONCE}>
                    <Button
                        className="pickup-point-list__show-more__button"
                        onClick={() => setPointsToDisplay(numberOfNearbyPointsToDisplayIncrement)}
                        variant="text-underline"
                    >
                        {TEXT.SHOW_LESS_POINTS}
                        <Icon name="chevron-up" />
                    </Button>
                </If>
            </div>
        </div>
    );
};

export default PickupPointList;
